import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { UserEvent } from 'src/app/features/smart-inbox/services/realtime.service';
import { CommonService } from 'src/app/shared/common.service';
import { NotificationEventType } from '../models/notification.models';
import { BrowserNotificationPort } from './browser-notification.port';
import {
  BatchEmission,
  BatchEntry,
  DESKTOP_NOTIFICATION_BATCH_WINDOW_MS,
} from './desktop-notification-batch';
import { DesktopNotificationBatchService } from './desktop-notification-batch.service';
import { DesktopDeferredQueueService } from './desktop-deferred-queue.service';
import { DesktopNotificationPreferenceService } from './desktop-notification-preference.service';
import { DesktopSuppressionContext, pathOf } from './desktop-suppression-context';
import { NotificationNavigatorService } from './notification-navigator.service';
import { DesktopNotificationSpec, descriptorFor } from './notification-event-registry';
import { adaptUserEvent } from './user-event-adapter';

// ─────────────────────────────────────────────────────────────────────────────
// DesktopNotificationService
// ─────────────────────────────────────────────────────────────────────────────
// The one place the phase's primitives meet: adapt the SSE event, resolve its
// descriptor, apply the opt-in / permission / eligibility / suppression gates,
// run the result through the batching window and hand it to the port.
// ─────────────────────────────────────────────────────────────────────────────

/** One tag for every grouped banner, so a new group replaces the previous one. */
export const DESKTOP_GROUPED_TAG = 'volta.orders.batch';

/**
 * Where a grouped banner navigates. A group covers several orders, so it cannot
 * deep-link to one; the list is the closest thing to all of them.
 */
export const GROUPED_NOTIFICATION_ROUTE: unknown[] = ['/quotations'];

const GROUPED_MIXED_TITLE_KEY = 'notifications.event.grouped_generic';

const GROUPED_TITLE_KEYS: Record<NotificationEventType, string> = {
  comment_mention: GROUPED_MIXED_TITLE_KEY,
  thread_assignment: GROUPED_MIXED_TITLE_KEY,
  order_created: GROUPED_MIXED_TITLE_KEY,
  order_status_changed: 'notifications.event.grouped_orders_ready',
  workflow_send_reply_failed: GROUPED_MIXED_TITLE_KEY,
};

/** How long an in-app toast stays up, matching the smart-inbox order toasts. */
export const NOTIFICATION_TOAST_DURATION_MS = 10_000;

/**
 * Which surface an event should use right now.
 *
 * - `none` — a Volta tab is focused on a route that refreshes itself for this event.
 * - `toast` — a Volta tab is focused elsewhere, so the user is looking at the app and an
 *   OS banner would talk over a window they can already see.
 * - `banner` — no Volta tab holds focus, so the OS is the only way to reach them.
 */
type NotificationSurface = 'none' | 'toast' | 'banner';

function specFor(eventType: NotificationEventType): DesktopNotificationSpec | null {
  return descriptorFor(eventType)?.desktop || null;
}

@Injectable({ providedIn: 'root' })
export class DesktopNotificationService {
  private openNotifications: Notification[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private port: BrowserNotificationPort,
    private preferences: DesktopNotificationPreferenceService,
    private suppression: DesktopSuppressionContext,
    private batch: DesktopNotificationBatchService,
    private deferred: DesktopDeferredQueueService,
    private translate: TranslateService,
    private authService: AuthService,
    private router: Router,
    private navigator: NotificationNavigatorService,
    private common: CommonService,
    private ngZone: NgZone,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  start(): void {
    this.suppression.startPublishing();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('focus', this.onReturn);
  }

  /** Teardown half of start(). Unused while the service lives as long as the page. */
  stop(): void {
    this.suppression.stopPublishing();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('focus', this.onReturn);
    this.cancelFlushTimer();
  }

  /**
   * A banner the user has already come back to is stale: the surface it points at
   * is on screen. It cannot reach the toasts other features raise independently.
   */
  private readonly onVisibilityChange = (): void => {
    if (!this.port.visible) {
      return;
    }
    this.openNotifications.forEach((notification) => notification.close());
    this.openNotifications = [];
    this.onReturn();
  };

  /**
   * A tab that is both visible and focused is the one the user is looking at, so it is
   * the one that owes them whatever was held while they were away. Visibility alone is
   * not enough — another window may be in front of a "visible" tab.
   */
  private readonly onReturn = (): void => {
    if (!this.port.visible || !this.port.focused) {
      return;
    }
    void this.safely(() => this.announceDeferred());
  };

  // ── Delivery ───────────────────────────────────────────────────────────────

  /** Resolves once the event has been gated. Never rejects. */
  deliver(event: UserEvent): Promise<void> {
    return this.safely(() => this.gate(event));
  }

  /**
   * The gate order is load-bearing: eligibility is decided before suppression and
   * before batching, so an ineligible event never opens a window and never lands
   * in a group.
   */
  private async gate(event: UserEvent): Promise<void> {
    const adapted = adaptUserEvent(event);
    if (!adapted) {
      return;
    }

    const spec = specFor(adapted.eventType);
    if (!spec) {
      return;
    }

    if (!spec.eligible(adapted.payload)) {
      return;
    }

    const surface = this.surfaceFor(spec);
    if (surface === 'none') {
      return;
    }
    // Both OS-level gates apply to the banner and only to the banner — see
    // `bannersAllowed()`. There is no surface left for this event right now, so hold it
    // for the user's return rather than dropping it: with banners off and nobody
    // focused, the bell badge was otherwise the entire feedback.
    //
    // The claim runs first because every tab receives the event; without it three tabs
    // would each queue the same one and the user would come back to "3 orders are
    // ready" for a single order. It deliberately never reaches `admit()` — the live
    // batching window is for events being shown now, and an event sitting in both
    // would be announced twice.
    if (surface === 'banner' && !this.bannersAllowed()) {
      if (await this.batch.claim(adapted.notificationId)) {
        await this.deferred.defer({
          eventType: adapted.eventType,
          payload: adapted.payload,
          notificationId: adapted.notificationId,
        });
      }
      return;
    }
    // The focused tab owns the toast. Every tab runs this gate, and a background one
    // dispatching a toast would render it into a view the user cannot see — so when the
    // surface is in-app, only the tab actually holding focus continues. With a banner
    // nobody is looking, and the claim below elects a single deliverer as before.
    if (surface === 'toast' && !(this.port.visible && this.port.focused)) {
      return;
    }

    const granted = await this.batch.claim(adapted.notificationId);
    const emission = granted
      ? this.batch.admit({ eventType: adapted.eventType, payload: adapted.payload })
      : null;
    this.scheduleFlush();

    if (emission) {
      this.emit(emission);
    }
  }

  /**
   * The app opt-in and the browser permission are both statements about the **OS
   * banner**, and about nothing else. The toggle is labelled "Browser notifications";
   * the permission belongs to the browser. An in-app toast is rendered by Angular into
   * a page the user is already looking at and needs neither.
   *
   * Running these two ahead of `surfaceFor()` is what made turning the toggle off — the
   * default, since an absent preference reads false — remove the only "your order is
   * ready" feedback the app has, `order_status_changed` having no other toast owner.
   */
  private bannersAllowed(): boolean {
    if (!this.preferences.isEnabled(this.currentUserId())) {
      return false;
    }
    return this.port.supported && this.port.permission === 'granted';
  }

  /**
   * The focused route is the globally published one, never this tab's own: a tab
   * hidden behind the focused orders list must not notify about that list.
   */
  private surfaceFor(spec: DesktopNotificationSpec): NotificationSurface {
    const focusedPath = this.suppression.focusedPath();
    if (focusedPath === null) {
      return 'banner';
    }
    return spec.suppressOnRoutes.includes(focusedPath) ? 'none' : 'toast';
  }

  /**
   * The registry's copy for an emission, shared by the live surfaces and the deferred
   * one, so an event announced on return reads exactly as it would have live.
   */
  private titleFor(emission: BatchEmission): string | null {
    if (emission.kind === 'single') {
      const spec = specFor(emission.entry.eventType);
      if (!spec) {
        return null;
      }
      return this.translate.instant(
        spec.titleKey(emission.entry.payload),
        spec.titleParams(emission.entry.payload),
      );
    }

    const titleKey = emission.eventType
      ? GROUPED_TITLE_KEYS[emission.eventType]
      : GROUPED_MIXED_TITLE_KEY;
    return this.translate.instant(titleKey, { count: emission.count });
  }

  private emit(emission: BatchEmission): void {
    const title = this.titleFor(emission);
    if (title === null) {
      return;
    }

    if (emission.kind === 'single') {
      const spec = specFor(emission.entry.eventType);
      if (!spec) {
        return;
      }
      this.show(title, spec.tag(emission.entry.payload), emission.entry, this.surfaceFor(spec));
      return;
    }

    // Re-decided here rather than carried from the gate: a group flushes up to 10s later,
    // by which time the user may have left or returned to the app.
    const spec = emission.eventType ? specFor(emission.eventType) : null;
    this.show(title, DESKTOP_GROUPED_TAG, null, spec ? this.surfaceFor(spec) : 'banner');
  }

  // ── What was held while the user was away ──────────────────────────────────

  /**
   * Always in-app, never a banner: an event is held precisely because the user has
   * banners off, and it is announced precisely because they have just come back.
   */
  private async announceDeferred(): Promise<void> {
    const emission = await this.deferred.drain();
    if (!emission) {
      return;
    }

    // This tab *is* the focused one — that is why it is draining — so its own route is
    // the focused route. Reading it directly rather than through the suppression context
    // avoids racing that context, which republishes on the very same focus event.
    if (this.selfReported(emission, pathOf(this.router.url))) {
      return;
    }

    const title = this.titleFor(emission);
    if (title === null) {
      return;
    }
    this.showToast(title, emission.kind === 'single' ? emission.entry : null);
  }

  /**
   * True when the route the user came back to already reports the event itself — the
   * same rule the live surfaces follow, applied at the moment of return rather than of
   * arrival.
   */
  private selfReported(emission: BatchEmission, path: string): boolean {
    const eventType = emission.kind === 'single' ? emission.entry.eventType : emission.eventType;
    if (!eventType) {
      // A mixed group has no single declaration to consult, so nothing suppresses it.
      return false;
    }
    const spec = specFor(eventType);
    return !!spec && spec.suppressOnRoutes.includes(path);
  }

  /**
   * The click handler never cancels the event: preventing the browser's own default is
   * what opens a new tab instead of reusing this one. It does not rely on that default
   * either — see the `window.focus()` call below.
   */
  private show(
    title: string,
    tag: string,
    target: BatchEntry | null,
    surface: NotificationSurface,
  ): void {
    if (surface === 'none') {
      return;
    }
    if (surface === 'toast') {
      this.showToast(title, target);
      return;
    }
    // `emit()` re-reads the surface deliberately, so a group admitted as a toast can
    // arrive here as a banner up to 10s later, after the user has left the app. Ask
    // again rather than trusting the decision the gate took.
    if (!this.bannersAllowed()) {
      return;
    }

    const notification = this.port.show(title, { tag });
    if (!notification) {
      return;
    }
    this.openNotifications = [...this.openNotifications, notification];

    notification.addEventListener('click', () => {
      // The spec says the user agent *should* bring the viewport into focus when the
      // click is not cancelled, and browsers honour that as a tab-level activation
      // inside an already-visible window — which is not the same as raising the window
      // the tab lives in. Asking explicitly is what surfaces a backgrounded Volta. The
      // click grants transient user activation, which is what makes the call permitted
      // here and would not be anywhere else.
      //
      // It is still only a request: an OS that refuses a foreground change to a
      // background process (Windows' foreground lock) flashes the taskbar instead. No
      // web API can go further from a page; only a service worker could.
      window.focus();

      this.ngZone.run(() => {
        if (target === null) {
          this.router.navigate(GROUPED_NOTIFICATION_ROUTE);
          return;
        }
        this.navigator.navigateTo(target.eventType, target.payload);
      });
    });
  }

  /**
   * In-app equivalent for a user who is already looking at Volta. Same copy and same
   * deep-link as the banner — `redirectTo` takes a URL, which is why the navigator
   * serializes the registry's route rather than this service rebuilding it.
   */
  private showToast(title: string, target: BatchEntry | null): void {
    const redirectTo =
      target === null
        ? this.router.serializeUrl(this.router.createUrlTree(GROUPED_NOTIFICATION_ROUTE))
        : this.navigator.urlFor(target.eventType, target.payload);

    this.common.showToast(title, 'INFO', {
      duration: NOTIFICATION_TOAST_DURATION_MS,
      verticalPosition: 'bottom',
      horizontalPosition: 'right',
      ...(redirectTo ? { redirectTo } : {}),
    });
  }

  private currentUserId(): number | null {
    return this.authService.getCurrentUser()?.id ?? null;
  }

  // ── Trailing-window backstop ───────────────────────────────────────────────

  /**
   * Only a backstop for a burst that simply stops: the primary path is the batch
   * store's lazy flush on arrival, which a throttled background timer cannot
   * delay.
   */
  private scheduleFlush(): void {
    this.cancelFlushTimer();
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.safely(() => {
        // Never drain a queue this tab cannot render. `flush()` empties the shared
        // pending list as a side effect of returning an emission, so a background tab
        // winning this race while the surface is a toast would consume the group and
        // render it where nobody can see it — and no other tab could recover it.
        // Guarding inside `show()` instead would turn that into silent loss.
        if (!this.canRenderNow()) {
          if (this.batch.hasPending()) {
            this.scheduleFlush();
          }
          return;
        }
        const emission = this.batch.flush();
        if (emission) {
          this.emit(emission);
          this.scheduleFlush();
        }
      });
    }, DESKTOP_NOTIFICATION_BATCH_WINDOW_MS);
  }

  /**
   * A banner is visible whichever tab opens it, so any tab may flush one. A toast is
   * only visible in the tab holding focus, so only that tab may drain the queue.
   */
  private canRenderNow(): boolean {
    if (this.suppression.focusedPath() === null) {
      return true;
    }
    return this.port.visible && this.port.focused;
  }

  private cancelFlushTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * A mirroring failure is per-event and must never reach the caller. The work may be
   * asynchronous, so a rejected promise has to be caught here too: an escaping rejection
   * would terminate the effect stream that calls in.
   */
  private async safely(work: () => void | Promise<void>): Promise<void> {
    try {
      await work();
    } catch {
      console.warn('[DesktopNotification] the notification could not be delivered');
    }
  }
}
